// PotreeViewer3 리팩토링 - 경로 기하학 순수 함수 모듈
// 지침: PotreeViewer3.jsx의 기존 동작은 유지한 채,
//       순수 계산 함수들만 물리적으로 이 파일로 이동한다.
//       (호출 방식/시그니처는 변경하지 않는다.)

import * as turf from "@turf/turf";

// NOTE:
// - 아래 함수들은 PotreeViewer3.jsx에서 그대로 복사해 왔으며,
//   구현 내용은 변경하지 않았다.
// - vehicleSpecs에 의존하는 함수(caculateturningRadius, caculateHeadlandWidth)는
//   현재 단계에서는 이동하지 않고, PotreeViewer3.jsx에 그대로 둔다.

// -----------------------------------------------------------------------------
// distancePointToSegment / findNearestEdge / makeTurnPoint / TurnPoint
// -----------------------------------------------------------------------------

export function distancePointToSegment(P, A, B) {
  const AP = [P[0] - A[0], P[1] - A[1]];
  const AB = [B[0] - A[0], B[1] - A[1]];

  const ab2 = AB[0] * AB[0] + AB[1] * AB[1];
  const ap_ab = AP[0] * AB[0] + AP[1] * AB[1];

  // segment projection t
  let t = ap_ab / ab2;
  t = Math.max(0, Math.min(1, t)); // segment clamp

  const closest = [A[0] + AB[0] * t, A[1] + AB[1] * t];

  const dx = P[0] - closest[0];
  const dy = P[1] - closest[1];
  return Math.sqrt(dx * dx + dy * dy);
}

export function findNearestEdge(point, polygon) {
  let minDist = Infinity;
  let bestIndex = -1;

  for (let i = 0; i < polygon.length - 1; i++) {
    const A = polygon[i];
    const B = polygon[i + 1];

    const d = distancePointToSegment(point, A, B);

    if (d < minDist) {
      minDist = d;
      bestIndex = i;
    }
  }

  return bestIndex;
}

// 회전 점 계산 함수
export function makeTurnPoint(outer, inner, R) {
  const dx = outer[0] - inner[0];
  const dy = outer[1] - inner[1];
  const L = Math.sqrt(dx * dx + dy * dy);

  const nx = dx / L;
  const ny = dy / L;

  return [inner[0] + nx * (R / 2), inner[1] + ny * (R / 2)];
}

export function TurnPoint(polygon, headland, turningRadius) {
  const turnPoints = [];

  for (let i = 0; i < headland.length - 1; i++) {
    const H = headland[i];
    const P = polygon[i];

    turnPoints.push(makeTurnPoint(P, H, turningRadius));
  }
  return turnPoints;
}

// -----------------------------------------------------------------------------
// generateHeadland - 헤드랜드/턴 영역 계산
// -----------------------------------------------------------------------------

export const generateHeadland = (fieldGeoJSON, bufferDistance = 10) => {
  try {
    // 원본 영역
    const fieldPolygon = turf.polygon(fieldGeoJSON.geometry.coordinates);

    // 투영좌표계를 지리좌표계로 변환
    const fieldPolygonWGS84 = turf.toWgs84(fieldPolygon);

    const headlandPolygonWGS84 = turf.buffer(fieldPolygonWGS84, -bufferDistance, {
      units: "meters",
    });

    const headlandPolygon = turf.toMercator(headlandPolygonWGS84);

    // 회랑 폴리곤 생성
    const turningPolygonWGS84 = turf.buffer(
      fieldPolygonWGS84,
      -bufferDistance / 2,
      {
        units: "meters",
      }
    );

    const turningPolygon = turf.toMercator(turningPolygonWGS84);

    return {
      work_area: fieldPolygon.geometry.coordinates[0],
      headland: headlandPolygon.geometry.coordinates[0],
      turning: turningPolygon.geometry.coordinates[0],
    };
  } catch (err) {
    console.warn("헤드랜드 생성 오류:", err);
    return null;
  }
};

// -----------------------------------------------------------------------------
// exportMeasurementToGeoJSON - 측정 영역을 GeoJSON Polygon으로 변환
// -----------------------------------------------------------------------------

export const exportMeasurementToGeoJSON = (measurement) => {
  const coords = measurement.points.map((p) => [p.position.x, p.position.y]);
  // 폴리곤 닫기
  coords.push(coords[0]);

  const area =
    parseFloat(measurement.areaLabel.text.replace(/[^\d.]/g, "")) || 0;

  return {
    type: "Feature",
    geometry: {
      type: "Polygon",
      coordinates: [coords],
    },
    properties: {
      name: measurement.name,
      area_m2: area,
    },
  };
};

